import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SearchBar, SummaryCard } from "./_shared";

export default function RelationshipsTab() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/students"), api.get("/users")]);
        setStudents(s.data || []);
        setUsers(u.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Build a map: guardian-key -> { user?, name, phone, email, students: [...], hasLogin }
  // Key priority: user_id if present, else name+phone lowercased.
  const usersById = Object.fromEntries(users.map(u => [u.id, u]));
  const groups = new Map();
  const orphanStudents = [];

  for (const s of students) {
    const gs = s.guardians || [];
    if (gs.length === 0) { orphanStudents.push(s); continue; }
    for (const g of gs) {
      const key = g.user_id || `nolog::${(g.name || "").toLowerCase()}::${(g.phone || "").toLowerCase()}`;
      let group = groups.get(key);
      if (!group) {
        const linkedUser = g.user_id ? usersById[g.user_id] : null;
        group = {
          key,
          user: linkedUser,
          hasLogin: !!linkedUser,
          name: linkedUser?.name || g.name || "(unnamed guardian)",
          phone: linkedUser?.phone || g.phone || "",
          email: linkedUser?.email || g.email || "",
          relation: g.relation || "Parent",
          students: [],
        };
        groups.set(key, group);
      }
      group.students.push({ id: s.id, name: s.name, relation: g.relation });
    }
  }

  // Also surface guardian-role users who exist but aren't linked to any student.
  for (const u of users) {
    if (!(u.roles || []).includes("guardian")) continue;
    if (groups.has(u.id)) continue;
    groups.set(u.id, {
      key: u.id,
      user: u,
      hasLogin: true,
      name: u.name,
      phone: u.phone || "",
      email: u.email || "",
      relation: "Parent",
      students: [],
    });
  }

  const allGroups = Array.from(groups.values()).sort((a, b) => {
    if (a.hasLogin !== b.hasLogin) return a.hasLogin ? -1 : 1;
    if (a.students.length !== b.students.length) return b.students.length - a.students.length;
    return a.name.localeCompare(b.name);
  });

  const s = q.trim().toLowerCase();
  const filtered = s
    ? allGroups.filter(g =>
        g.name.toLowerCase().includes(s)
        || (g.phone || "").toLowerCase().includes(s)
        || (g.email || "").toLowerCase().includes(s)
        || g.students.some(st => st.name.toLowerCase().includes(s))
      )
    : allGroups;

  const withLoginCount = allGroups.filter(g => g.hasLogin).length;
  const withoutLoginCount = allGroups.length - withLoginCount;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Parents / Guardians" value={allGroups.length} />
        <SummaryCard label="With login account" value={withLoginCount} tone="success" />
        <SummaryCard label="No login (embedded only)" value={withoutLoginCount} tone="warn" />
        <SummaryCard label="Students without parent" value={orphanStudents.length} tone={orphanStudents.length ? "warn" : "muted"} />
      </div>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base font-display tracking-tight">
              Parent ↔ Student relationships ({filtered.length}{q && ` / ${allGroups.length}`})
            </CardTitle>
          </div>
          <SearchBar value={q} onChange={setQ} placeholder="Search by parent name, phone, email or child name…" testId="relationships-search" />
        </CardHeader>
        <CardContent className="p-0">
          {loading && <div className="p-6 text-sm text-muted-foreground">Loading relationships…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              {q ? `No relationships match "${q}"` : "No parent/guardian records yet."}
            </div>
          )}
          <div className="divide-y divide-border">
            {filtered.map(g => (
              <div key={g.key} className="px-5 py-4 space-y-2" data-testid={`relationship-row-${g.key}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-sm">{g.name}</span>
                      <Badge variant="outline" className="text-[10px]">{g.relation}</Badge>
                      {g.hasLogin ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Login account</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">No login</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {g.phone || "no phone"}
                      {g.email && ` · ${g.email}`}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {g.students.length} {g.students.length === 1 ? "child" : "children"}
                  </div>
                </div>
                {g.students.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {g.students.map(st => (
                      <button
                        key={st.id}
                        onClick={() => navigate(`/students/${st.id}`)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-primary/5 text-primary hover:bg-primary/10 transition"
                        data-testid={`relationship-child-${st.id}`}
                      >
                        {st.name}
                        {st.relation && st.relation !== g.relation && <span className="opacity-60">({st.relation})</span>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground italic pt-1">No children linked yet &mdash; this guardian account has a login but isn&rsquo;t in any student&rsquo;s guardians list.</div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {orphanStudents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-display tracking-tight">Students with no parent on file ({orphanStudents.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {orphanStudents.map(s2 => (
                <button
                  key={s2.id}
                  onClick={() => navigate(`/students/${s2.id}`)}
                  className="w-full px-5 py-3 flex items-center gap-3 hover:bg-muted/40 text-left"
                  data-testid={`orphan-student-${s2.id}`}
                >
                  <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center font-mono text-xs">{(s2.name || "?").slice(0,2).toUpperCase()}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{s2.name}</div>
                    <div className="text-xs text-muted-foreground">Open the student to add a guardian →</div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
